import React from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch } from '@hooks/index';
import Table from '@components/common/react-table/Table';
import { openTicketTypeForm, openTicketDrawer, setTicketTypeDetails, showTktCategoryNavigation } from '@actions/ticketMgmtPortal/ticketMgmtPortalActionCreators';
import { STATUS_COLOR_CODES } from '@constants/common/miscellaneous';
import { TICKET_TYPE_TABLE_FIELDS, DEFAULT_EXPIRATION_DATE_LIMIT, DEFAULT_EXPIRATION_DATE } from '../forms/ticketFormConfigs';

const StyledAnchor = styled.a`
    text-decoration: none;
    font-weight: 600;
    &:hover {
        text-decoration: none;
    }
`;

const StyledTableHolder = styled.div`
    tr td:first-child {
        padding-left: 20px;
    }
    th {
        padding-top: 12px;
        padding-bottom: 12px;
    }
    tbody > tr > td {
        padding-top: 12px;
        padding-bottom: 12px;
        cursor: pointer;
        .table-cell {
            max-width: 155px;
            display: inline-block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
`;

const StyledNoRecord = styled.div`
    margin: 10px 0;
    text-align: center;
`;

export const TicketTypesTable = (props) => {
    const { ticketTypesData, pageSize, showCategoryColumn } = props;
    const dispatch = useAppDispatch();
    const getTicketTypeById = (e, id, showPrevNext) => {
        e.preventDefault();
        const tktTypeItem =  ticketTypesData?.find(ticketType => ticketType.id === id);
        if (!tktTypeItem) {
            return;
        } else {
            dispatch(setTicketTypeDetails(tktTypeItem));
        }
        if(showPrevNext) {
            dispatch(showTktCategoryNavigation(true));    
        } else {
            dispatch(openTicketTypeForm(true));
        }
        dispatch(openTicketDrawer(true));
    };

    const categoryColumn = showCategoryColumn ? [{
        Header: 'ticket_management.ticketTypes.table.header.ticketCategory',
        accessor: TICKET_TYPE_TABLE_FIELDS.CATEGORY,
        Cell: ({row}) => {
            const { id, category } = row.original;
            const cellContent = category || '-';
            return (<StyledAnchor href='#' id={id} className='table-cell' title={cellContent} onClick={(event) => getTicketTypeById(event, id, true)} data-testid={`category-cell-${id}`} aria-label={`Category: ${cellContent}`}>{cellContent}</StyledAnchor>);
        }
    }] : []; 
    const columns = [
        {
            Header: () => (
                <span className="usa-sr-only">
                    <FormattedMessage id="ticket.table_title.status" />
                </span>
            ),
            accessor: TICKET_TYPE_TABLE_FIELDS.STATUS,
            width: '50px',
            Cell: ({ row }) => {
                const status = row.original.status;
                return (<div style={{ backgroundColor: STATUS_COLOR_CODES[(status)] || 'grey' }} className='tooltip--circle' title={status} data-testid={`status-cell-${row.original.id}`} aria-label={`Status: ${status}`}></div>);
            }
        },
        ...categoryColumn,
        {
            Header: 'ticket_management.ticketTypes.table.header.ticketType',
            accessor: TICKET_TYPE_TABLE_FIELDS.NAME,
            Cell: ({row}) => {
                const { id, type } = row.original;
                const cellContent = type || '-';
                return (
                    <StyledAnchor href='#' id={id} className='table-cell' title={cellContent} onClick={(event) => getTicketTypeById(event, id, false)} data-testid={`ticket-type-cell-${id}`} aria-label={`Ticket Type: ${cellContent}`}>{cellContent}</StyledAnchor>
                );
            }, 
        },
        {
            Header: 'ticket_management.form_header.workgroup',
            accessor: TICKET_TYPE_TABLE_FIELDS.WORKGROUP,
            Cell: ({row}) => {
                const { id, workgroup } = row.original;
                const cellContent = workgroup?.label || '-';
                return (<span id={id} className='table-cell' title={cellContent} data-testid={`workgroup-cell-${id}`} aria-label={`Workgroup: ${cellContent}`}>{cellContent}</span>);
            }
        },
        {
            Header: 'ticket_management.form_header.due_date',
            accessor: TICKET_TYPE_TABLE_FIELDS.DUE_DATE,
            width: '70px',
            Cell: ({row}) => {
                const { id, expDate } = row.original;
                const cellContent = expDate ? expDate >= DEFAULT_EXPIRATION_DATE_LIMIT ? DEFAULT_EXPIRATION_DATE : expDate : '-';
                return (<span id={id} title={cellContent} data-testid={`due-date-cell-${id}`} aria-label={`Due Date: ${cellContent}`}>{cellContent}</span>);
            }
        },
        {
            Header: 'ticket_management.form_header.permission',
            accessor: TICKET_TYPE_TABLE_FIELDS.PERMISSION_NAME,
            Cell: ({row}) => {
                const { id, permission } = row.original;
                const cellContent = permission || '-';
                return (<span id={id} className="table-cell" title={cellContent} data-testid={`permission-cell-${id}`} aria-label={`Permission: ${cellContent}`}>{cellContent}</span>);
            },
        },
        {
            Header: 'ticket_management.form_header.priority',
            accessor: TICKET_TYPE_TABLE_FIELDS.PRIORITY,
            width: '70px',
            Cell: ({row}) => {
                const { id, defaultPriority } = row.original;
                const cellContent = defaultPriority || '-';
                return (<span id={id} title={cellContent} data-testid={`priority-cell-${id}`} aria-label={`Priority: ${cellContent}`}>{cellContent}</span>);
            }
        }
    ];

    return (
        <StyledTableHolder data-testid="ticket-types-table-holder">
            <Table
                id="ticket-types-table"
                className="ticket-types-table"
                columns={columns}
                data={ticketTypesData?.length ? ticketTypesData : []}
                isClientPagination
                hasPagination
                hasBorders
                variant="simple"
                totalCount={ticketTypesData?.length || 0}
                clientPageSize={pageSize}
                rowsPerPage={pageSize}
                maxNumPages={pageSize}
                emptyMessage={
                    <StyledNoRecord data-testid="ticket-types-table-no-record" aria-live="polite">
                        <FormattedMessage id='norecordsfound' />
                    </StyledNoRecord>
                }
            /> 
        </StyledTableHolder>  
    );
};